import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { billingService, planService, userService, whatsappAccountService } from '../../services/api';
import { INK, MUTED, RULE } from '../analytics/analyticsUi';

const CYCLES = { monthly: { label: 'Monthly', months: 1 }, quarterly: { label: 'Quarterly', months: 3 }, annually: { label: 'Annually', months: 12 } };
const STATUSES = {
  active: 'Active',
  pending: 'Awaiting payment',
  cancelled: 'Cancelled',
  expired: 'Expired'
};
const PAYMENT_METHODS = {
  manual: 'Manual / bank transfer',
  upi: 'UPI',
  net_banking: 'Net banking',
  credit_card: 'Credit card',
  debit_card: 'Debit card',
  wallet: 'Wallet',
  offline: 'Offline payment'
};

const toInputDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const addMonths = (dateValue, months) => {
  const [y, m, d] = dateValue.split('-').map(Number);
  return toInputDate(new Date(y, m - 1 + months, d));
};

const idOf = (value) => (value && typeof value === 'object' ? value._id : value);

/**
 * Assign a subscription to any customer, or edit an existing one.
 * props: open, onClose, onSaved(subscription), subscription (edit mode), initialUserId (assign mode)
 */
const SubscriptionForm = ({ open, onClose, onSaved, subscription, initialUserId }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const editing = !!subscription;

  const [users, setUsers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [form, setForm] = useState(null);
  const [amountTouched, setAmountTouched] = useState(false);
  const [endTouched, setEndTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load customers, accounts and plans once the dialog opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      try {
        setLoadingOptions(true);
        const [usersRes, accountsRes, plansRes] = await Promise.all([
          userService.getUsers({ page: 1, limit: 1000 }),
          whatsappAccountService.getAccounts(1, 1000),
          planService.getPlans({ all: true })
        ]);
        if (cancelled) return;
        setUsers(usersRes.data || []);
        setAccounts(accountsRes.data || []);
        setPlans((plansRes.data || []).sort((a, b) => (a.price || 0) - (b.price || 0)));
      } catch (err) {
        if (!cancelled) setError('Customers, accounts or plans could not be loaded. Close the form and try again.');
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [open]);

  // Reset the form each time it opens
  useEffect(() => {
    if (!open) return;
    setError(null);
    setAmountTouched(editing);
    setEndTouched(editing);
    if (subscription) {
      setForm({
        userId: idOf(subscription.user) || '',
        whatsappAccount: idOf(subscription.whatsappAccount) || '',
        plan: String(subscription.plan || '').toLowerCase(),
        billingCycle: subscription.billingCycle || 'monthly',
        amount: subscription.amount ?? '',
        startDate: toInputDate(subscription.startDate),
        endDate: toInputDate(subscription.endDate),
        status: subscription.status || 'active',
        autoRenew: subscription.autoRenew !== false,
        paymentMethod: subscription.paymentMethod || 'manual',
        transactionId: subscription.transactionId || '',
        adminNotes: subscription.adminNotes || ''
      });
    } else {
      const start = toInputDate(new Date());
      setForm({
        userId: initialUserId || '',
        whatsappAccount: '',
        plan: '',
        billingCycle: 'monthly',
        amount: '',
        startDate: start,
        endDate: addMonths(start, 1),
        status: 'active',
        autoRenew: true,
        paymentMethod: 'manual',
        transactionId: '',
        adminNotes: ''
      });
    }
  }, [open, subscription, initialUserId, editing]);

  const customerAccounts = useMemo(
    () => (form ? accounts.filter((a) => String(idOf(a.user)) === String(form.userId)) : []),
    [accounts, form]
  );

  // Pick the customer's only account automatically
  useEffect(() => {
    if (!form || editing || form.whatsappAccount || customerAccounts.length !== 1) return;
    setForm((prev) => ({ ...prev, whatsappAccount: customerAccounts[0]._id }));
  }, [customerAccounts, form, editing]);

  if (!form) return null;

  const selectedPlan = plans.find((p) => p.name.toLowerCase() === form.plan);
  const months = CYCLES[form.billingCycle]?.months || 1;
  const suggestedAmount = selectedPlan ? (selectedPlan.price || 0) * months : null;

  const update = (patch) => {
    setForm((prev) => {
      const next = { ...prev, ...patch };
      // Keep amount and end date in step with plan and cycle until edited by hand
      if (!amountTouched && ('plan' in patch || 'billingCycle' in patch)) {
        const plan = plans.find((p) => p.name.toLowerCase() === next.plan);
        if (plan) next.amount = (plan.price || 0) * (CYCLES[next.billingCycle]?.months || 1);
      }
      if (!endTouched && ('startDate' in patch || 'billingCycle' in patch) && next.startDate) {
        next.endDate = addMonths(next.startDate, CYCLES[next.billingCycle]?.months || 1);
      }
      if ('userId' in patch && patch.userId !== prev.userId) next.whatsappAccount = '';
      return next;
    });
  };

  const selectedUser = users.find((u) => String(u._id) === String(form.userId)) || null;
  const problems = [];
  if (!form.userId) problems.push('Choose a customer');
  if (!form.whatsappAccount) problems.push('Choose a WhatsApp account');
  if (!form.plan) problems.push('Choose a plan');
  if (form.amount === '' || Number(form.amount) < 0) problems.push('Enter the amount');
  if (!form.startDate || !form.endDate) problems.push('Enter the start and end dates');
  else if (form.endDate <= form.startDate) problems.push('The end date must be after the start date');

  const save = async () => {
    if (problems.length) {
      setError(problems[0]);
      return;
    }
    const payload = {
      whatsappAccount: form.whatsappAccount,
      plan: form.plan,
      billingCycle: form.billingCycle,
      amount: Number(form.amount),
      currency: selectedPlan?.currency || 'INR',
      startDate: form.startDate,
      endDate: form.endDate,
      status: form.status,
      autoRenew: form.autoRenew,
      paymentMethod: form.paymentMethod,
      transactionId: form.transactionId.trim(),
      adminNotes: form.adminNotes.trim()
    };
    try {
      setSaving(true);
      setError(null);
      const response = editing
        ? await billingService.updateBilling(subscription._id, payload)
        : await billingService.createBilling(payload);
      onSaved(response.data, editing);
    } catch (err) {
      setError(err.response?.data?.error || 'The subscription could not be saved. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const customerLabel = (u) => `${u.company ? `${u.company}, ` : ''}${u.name} (${u.email})`;

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ color: INK, fontWeight: 600 }}>
        {editing ? 'Edit subscription' : 'Assign subscription'}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <Box sx={{ display: 'grid', gap: 2.25, pt: 1 }}>
          <Autocomplete
            size="small"
            options={users}
            loading={loadingOptions}
            value={selectedUser}
            onChange={(e, u) => update({ userId: u ? u._id : '' })}
            isOptionEqualToValue={(o, v) => String(o._id) === String(v._id)}
            getOptionLabel={customerLabel}
            disabled={editing}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Customer"
                helperText={editing ? 'To give the plan to another customer, move it to one of their accounts below.' : undefined}
              />
            )}
          />

          <FormControl size="small" fullWidth disabled={!form.userId && !editing}>
            <InputLabel id="sub-account">WhatsApp account</InputLabel>
            <Select
              labelId="sub-account"
              label="WhatsApp account"
              value={form.whatsappAccount}
              onChange={(e) => update({ whatsappAccount: e.target.value })}
            >
              {(editing ? accounts : customerAccounts).map((a) => (
                <MenuItem key={a._id} value={a._id}>
                  {a.verifiedName || a.businessName}, {a.phoneNumber}
                  {editing && idOf(a.user) !== form.userId && a.user?.name ? ` (${a.user.company || a.user.name})` : ''}
                </MenuItem>
              ))}
            </Select>
            {form.userId && !loadingOptions && !customerAccounts.length && !editing && (
              <Typography variant="caption" sx={{ color: '#C77700', mt: 0.5 }}>
                This customer has no WhatsApp account yet. Connect one before assigning a plan.
              </Typography>
            )}
          </FormControl>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="sub-plan">Plan</InputLabel>
              <Select labelId="sub-plan" label="Plan" value={form.plan} onChange={(e) => update({ plan: e.target.value })}>
                {plans.map((p) => (
                  <MenuItem key={p._id} value={p.name.toLowerCase()}>
                    {p.name}, ₹{(p.price || 0).toLocaleString('en-IN')}/month{p.isActive === false ? ' (inactive)' : ''}
                  </MenuItem>
                ))}
                {form.plan && !selectedPlan && <MenuItem value={form.plan}>{form.plan} (not in plans)</MenuItem>}
              </Select>
            </FormControl>
            <TextField
              size="small"
              type="number"
              label="Amount per cycle"
              value={form.amount}
              onChange={(e) => { setAmountTouched(true); update({ amount: e.target.value }); }}
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              inputProps={{ min: 0, step: 1 }}
              helperText={suggestedAmount !== null && Number(form.amount) !== suggestedAmount
                ? `Plan price for this cycle: ₹${suggestedAmount.toLocaleString('en-IN')}`
                : ' '}
            />
          </Box>

          <Box>
            <Typography variant="body2" sx={{ color: MUTED, mb: 0.75 }}>Billing cycle</Typography>
            <ToggleButtonGroup exclusive size="small" value={form.billingCycle} onChange={(e, v) => v && update({ billingCycle: v })}>
              {Object.entries(CYCLES).map(([value, c]) => (
                <ToggleButton key={value} value={value} sx={{ textTransform: 'none', px: 2 }}>{c.label}</ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              size="small"
              type="date"
              label="Starts on"
              value={form.startDate}
              onChange={(e) => update({ startDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              type="date"
              label="Ends on"
              value={form.endDate}
              onChange={(e) => { setEndTouched(true); update({ endDate: e.target.value }); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: form.startDate }}
              helperText="Renews on this date if auto-renew is on"
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel id="sub-status">Status</InputLabel>
              <Select labelId="sub-status" label="Status" value={form.status} onChange={(e) => update({ status: e.target.value })}>
                {Object.entries(STATUSES)
                  .filter(([value]) => editing || ['active', 'pending'].includes(value))
                  .map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel id="sub-payment">Payment method</InputLabel>
              <Select labelId="sub-payment" label="Payment method" value={form.paymentMethod} onChange={(e) => update({ paymentMethod: e.target.value })}>
                {Object.entries(PAYMENT_METHODS).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          <FormControlLabel
            control={<Switch checked={form.autoRenew} onChange={(e) => update({ autoRenew: e.target.checked })} />}
            label={<Typography variant="body2" sx={{ color: INK }}>Renew automatically at the end of each cycle</Typography>}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField
              size="small"
              label="Transaction or reference ID"
              value={form.transactionId}
              onChange={(e) => update({ transactionId: e.target.value })}
              helperText="Optional, e.g. UTR number"
            />
            <TextField
              size="small"
              label="Admin notes"
              value={form.adminNotes}
              onChange={(e) => update({ adminNotes: e.target.value })}
              helperText="Only admins see this"
            />
          </Box>

          {!editing && form.status === 'active' && (
            <Box sx={{ bgcolor: '#F6F8FA', borderRadius: 1.5, p: 1.5, border: `1px solid ${RULE}` }}>
              <Typography variant="body2" sx={{ color: MUTED }}>
                The plan starts straight away as active and the customer is notified. Choose "Awaiting payment" if the payment hasn't arrived yet.
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={save}
          disabled={saving || loadingOptions}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Assign subscription'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubscriptionForm;
